import { parseISO, addDays, isWithinInterval } from 'date-fns';
import type { ChartType } from '@/features/report-snapshots/types';
import type { ColumnDefinition, CellType } from '@/features/report-snapshots/api/schema';
import { calculateFractionValue } from '@/features/report-snapshots/formatters/ratio';

export function isReportExpiring(expiresAt: string): boolean {
  const targetDate = parseISO(expiresAt);
  const today = new Date();

  return isWithinInterval(targetDate, {
    start: today,
    end: addDays(today, 2),
  });
}

export function isDataTable(visualizationType: ChartType): boolean {
  return visualizationType === 'data-table';
}

export function isTabularChart(visualizationType: ChartType): boolean {
  return visualizationType === 'tabular';
}

export function isCounterChart(visualizationType: ChartType): boolean {
  return visualizationType === 'counter';
}

export function isBannerChart(visualizationType: ChartType): boolean {
  return visualizationType === 'banner';
}

export function isHeatmapChart(visualizationType: ChartType): boolean {
  return visualizationType === 'heatmap';
}

// we need to consider for fraction(numerator, denominator) number and null values here
export function normalizeCellValue(
  value: CellType | undefined,
  columnDefinition: ColumnDefinition,
): number | null {
  const { unit, fractionUnit } = columnDefinition;

  if (value === null || value === undefined) {
    return null;
  }

  if (['ratio', 'percent'].includes(unit)) {
    const fraction = value as {
      numerator: number;
      denominator: number;
    };
    const unitForFraction = fractionUnit ?? {
      numerator: 'value',
      denominator: 'value',
    };
    const fractionForCalculation = {
      numerator: { value: fraction.numerator, unit: unitForFraction.numerator },
      denominator: { value: fraction.denominator, unit: unitForFraction.denominator },
    };
    const calculated = calculateFractionValue(fractionForCalculation);
    return Number.isFinite(calculated) ? calculated : null;
  }

  return value as number;
}
